import { createBrowserClient } from '@supabase/ssr'
import { processLock } from '@supabase/supabase-js'
import { Database } from './types'
import { createTimeoutFetch } from './timeoutFetch'

// A hung auth request holds the auth lock and blocks every authenticated query
// in this tab. When our fetch timeout kills such a request, offer the user the
// clear-and-reload recovery – a reload tears down the JS context and frees the
// lock immediately instead of waiting for the OS to kill the socket (which can
// take hours on mobile). Debounced so a burst of timeouts
// shows at most one recovery prompt.
let lastAuthTimeoutDispatch = 0
const AUTH_TIMEOUT_DEBOUNCE_MS = 30_000

const timeoutFetch = createTimeoutFetch({
  onAuthTimeout: () => {
    if (typeof window === 'undefined') return
    const now = Date.now()
    if (now - lastAuthTimeoutDispatch < AUTH_TIMEOUT_DEBOUNCE_MS) return
    lastAuthTimeoutDispatch = now
    window.dispatchEvent(new CustomEvent('auth:session-restore-failed'))
  },
})

// Auth operations are serialised with a PER-TAB lock (auth-js `processLock`),
// not the cross-tab Web Lock auth-js uses by default. The Web Lock is shared by
// every tab of the browser profile, and Android pauses background tabs: a
// paused tab's queued lock requests keep being granted the lock each time it
// frees and can never release it, so the live tab waited ~20 s per request
// even with a steal-after-timeout wrapper, until the old tab was closed
// (2026-09-17 and again 2026-09-24, Samsung Internet). With an in-tab lock no
// tab can starve another. Tabs still share the session through storage, and
// concurrent refreshes from two tabs – which the middleware already does
// server-side with no lock at all – are covered by Supabase's refresh-token
// reuse interval.
//
// Known noise: processLock (auth-js 2.95) never clears its acquire-timeout
// timer, so it logs `Lock "…" acquisition timed out after 10000ms` (or 0ms for
// the auto-refresh tick) AFTER operations that already succeeded. Those
// warnings are not a jam; a real one surfaces as a failed request.
//
// Create browser client - @supabase/ssr enforces PKCE flow (hardcoded in
// createBrowserClient, overrides any flowType setting) and handles
// code_verifier storage automatically via cookies and localStorage.
// `global.fetch` is wrapped to cap request duration so a hung auth request can
// never hold the auth lock indefinitely (see timeoutFetch.ts).
export const createClient = () =>
  createBrowserClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        lock: processLock,
      },
      global: {
        fetch: timeoutFetch,
      },
    }
  )

export const supabase = createClient()
